import React, { useState, useEffect } from 'react';
import {
  Box,
  IconButton,
  Slider,
  FormControlLabel,
  Switch,
  Select,
  MenuItem,
  Tooltip,
  Paper,
  Typography,
  Divider,
  Button,
} from '@mui/material';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import VolumeDownIcon from '@mui/icons-material/VolumeDown';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import NotificationsOffIcon from '@mui/icons-material/NotificationsOff';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { useAudioNotification } from '../../hooks/useAudioNotification';

/**
 * AudioNotificationSettings Component - PREMIUM EDITION
 * High-quality professional notification sounds
 */
const AudioNotificationSettings = ({ compact = false }) => {
  const {
    playNotification,
    setVolume: updateVolume,
    setSoundEnabled,
    testSound,
    getSettings,
  } = useAudioNotification();

  const [settings, setSettings] = useState(() => getSettings());
  const [localVolume, setLocalVolume] = useState(settings.volume);

  useEffect(() => {
    const updated = getSettings();
    setSettings(updated);
    setLocalVolume(updated.volume);
  }, [getSettings]);

  const handleVolumeChange = (event, newValue) => {
    setLocalVolume(newValue);
    updateVolume(newValue);
  };

  const handleToggleSound = () => {
    setSoundEnabled(!settings.enabled);
    setSettings(prev => ({
      ...prev,
      enabled: !prev.enabled,
    }));
  };

  const handleSoundTypeChange = (event) => {
    const newSoundType = event.target.value;
    setSettings(prev => ({
      ...prev,
      soundType: newSoundType,
    }));
  };

  const handleTestSound = () => {
    testSound(settings.soundType);
  };

  // Compact mode
  if (compact) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Tooltip title={settings.enabled ? 'Sound enabled' : 'Sound disabled'}>
          <IconButton
            size="small"
            onClick={handleToggleSound}
            sx={{
              color: settings.enabled ? '#1ABC9C' : 'rgba(255,255,255,0.5)',
              '&:hover': {
                backgroundColor: 'rgba(26,188,156,0.1)',
              },
            }}
          >
            {settings.enabled ? (
              <NotificationsActiveIcon fontSize="small" />
            ) : (
              <NotificationsOffIcon fontSize="small" />
            )}
          </IconButton>
        </Tooltip>

        {settings.enabled && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <VolumeDownIcon sx={{ fontSize: '0.9rem', color: 'rgba(255,255,255,0.5)' }} />
            <Slider
              value={localVolume}
              onChange={handleVolumeChange}
              min={0}
              max={1}
              step={0.1}
              sx={{
                width: 60,
                color: '#1ABC9C',
                '& .MuiSlider-thumb': {
                  width: 12,
                  height: 12,
                },
              }}
            />
            <VolumeUpIcon sx={{ fontSize: '0.9rem', color: '#1ABC9C' }} />
          </Box>
        )}
      </Box>
    );
  }

  // Full mode
  return (
    <Paper
      sx={{
        p: 2.5,
        backgroundColor: '#1e2a38',
        border: '1px solid rgba(26,188,156,0.2)',
        borderRadius: '12px',
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        {settings.enabled ? (
          <NotificationsActiveIcon sx={{ color: '#1ABC9C' }} />
        ) : (
          <NotificationsOffIcon sx={{ color: '#95a5a6' }} />
        )}
        <Typography
          variant="subtitle1"
          sx={{
            color: 'white',
            fontWeight: 600,
            fontSize: '1rem',
          }}
        >
          Premium Notifications
        </Typography>
      </Box>

      <Divider sx={{ borderColor: 'rgba(26,188,156,0.2)', mb: 2 }} />

      {/* Enable/Disable Toggle */}
      <FormControlLabel
        control={
          <Switch
            checked={settings.enabled}
            onChange={handleToggleSound}
            sx={{
              color: '#1ABC9C',
              '&.Mui-checked': {
                color: '#1ABC9C',
              },
            }}
          />
        }
        label={
          <Typography sx={{ color: 'white', fontSize: '0.9rem' }}>
            {settings.enabled ? 'Notifications Enabled' : 'Notifications Disabled'}
          </Typography>
        }
        sx={{ mb: 2 }}
      />

      {settings.enabled && (
        <>
          {/* Sound Selection */}
          <Box sx={{ mb: 2.5 }}>
            <Typography
              variant="caption"
              sx={{
                color: 'rgba(255,255,255,0.7)',
                display: 'block',
                mb: 1,
                fontSize: '0.75rem',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
              }}
            >
              Premium Notification Sound
            </Typography>
            <Select
              value={settings.soundType}
              onChange={handleSoundTypeChange}
              size="small"
              sx={{
                width: '100%',
                color: 'white',
                backgroundColor: 'rgba(255,255,255,0.08)',
                borderRadius: '8px',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(255,255,255,0.2)',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(26,188,156,0.5)',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#1ABC9C',
                },
                '& .MuiSvgIcon-root': {
                  color: 'rgba(255,255,255,0.7)',
                },
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    backgroundColor: '#1e2a38',
                    color: 'white',
                    border: '1px solid rgba(26,188,156,0.2)',
                    borderRadius: '8px',
                    backdropFilter: 'blur(10px)',
                    '& .MuiMenuItem-root': {
                      '&:hover': {
                        backgroundColor: 'rgba(26,188,156,0.1)',
                      },
                      '&.Mui-selected': {
                        backgroundColor: 'rgba(26,188,156,0.2)',
                        '&:hover': {
                          backgroundColor: 'rgba(26,188,156,0.3)',
                        },
                      },
                    },
                  },
                },
              }}
            >
              <MenuItem value="discord">
                <Box>
                  <Typography sx={{ fontWeight: 600 }}>Discord</Typography>
                  <Typography sx={{ fontSize: '0.75rem', opacity: 0.7 }}>Modern & Sophisticated</Typography>
                </Box>
              </MenuItem>
              <MenuItem value="slack">
                <Box>
                  <Typography sx={{ fontWeight: 600 }}>Slack</Typography>
                  <Typography sx={{ fontSize: '0.75rem', opacity: 0.7 }}>Subtle & Distinctive</Typography>
                </Box>
              </MenuItem>
              <MenuItem value="bell">
                <Box>
                  <Typography sx={{ fontWeight: 600 }}>Bell</Typography>
                  <Typography sx={{ fontSize: '0.75rem', opacity: 0.7 }}>Elegant & Classic</Typography>
                </Box>
              </MenuItem>
              <MenuItem value="chime">
                <Box>
                  <Typography sx={{ fontWeight: 600 }}>Chime</Typography>
                  <Typography sx={{ fontSize: '0.75rem', opacity: 0.7 }}>Musical & Harmonious</Typography>
                </Box>
              </MenuItem>
              <MenuItem value="glass">
                <Box>
                  <Typography sx={{ fontWeight: 600 }}>Glass</Typography>
                  <Typography sx={{ fontSize: '0.75rem', opacity: 0.7 }}>Clean & Modern</Typography>
                </Box>
              </MenuItem>
              <MenuItem value="elegant">
                <Box>
                  <Typography sx={{ fontWeight: 600 }}>Elegant</Typography>
                  <Typography sx={{ fontSize: '0.75rem', opacity: 0.7 }}>Smooth & Premium</Typography>
                </Box>
              </MenuItem>
            </Select>
            <Typography
              variant="caption"
              sx={{
                color: 'rgba(255,255,255,0.5)',
                display: 'block',
                mt: 1,
                fontSize: '0.7rem',
              }}
            >
              Advanced synthesis with professional-grade audio
            </Typography>
          </Box>

          {/* Volume Control */}
          <Box sx={{ mb: 2.5 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: 1,
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: 'rgba(255,255,255,0.7)',
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                }}
              >
                Volume Level
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: '#1ABC9C',
                  fontSize: '0.8rem',
                  fontWeight: 600,
                }}
              >
                {Math.round(localVolume * 100)}%
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <VolumeDownIcon
                sx={{ fontSize: '1.1rem', color: 'rgba(255,255,255,0.5)' }}
              />
              <Slider
                value={localVolume}
                onChange={handleVolumeChange}
                min={0}
                max={1}
                step={0.05}
                sx={{
                  flex: 1,
                  color: '#1ABC9C',
                  height: 5,
                  '& .MuiSlider-thumb': {
                    width: 16,
                    height: 16,
                    backgroundColor: '#1ABC9C',
                    border: '2px solid #243447',
                    boxShadow: '0 2px 8px rgba(26,188,156,0.3)',
                    '&:hover': {
                      boxShadow: '0 2px 12px rgba(26,188,156,0.5)',
                    },
                  },
                  '& .MuiSlider-rail': {
                    backgroundColor: 'rgba(255,255,255,0.1)',
                  },
                  '& .MuiSlider-track': {
                    backgroundColor: '#1ABC9C',
                  },
                }}
              />
              <VolumeUpIcon
                sx={{ fontSize: '1.1rem', color: '#1ABC9C' }}
              />
            </Box>
          </Box>

          {/* Preview Button */}
          <Button
            onClick={handleTestSound}
            variant="outlined"
            size="small"
            startIcon={<PlayArrowIcon />}
            sx={{
              width: '100%',
              color: '#1ABC9C',
              borderColor: 'rgba(26,188,156,0.3)',
              backgroundColor: 'transparent',
              textTransform: 'none',
              fontSize: '0.9rem',
              fontWeight: 600,
              transition: 'all 0.2s ease',
              '&:hover': {
                borderColor: '#1ABC9C',
                backgroundColor: 'rgba(26,188,156,0.1)',
              },
            }}
          >
            Preview Sound
          </Button>

          {/* Info */}
          <Typography
            variant="caption"
            sx={{
              color: 'rgba(255,255,255,0.4)',
              display: 'block',
              textAlign: 'center',
              mt: 1.5,
              fontSize: '0.65rem',
            }}
          >
            Professional-grade audio synthesis • Zero latency • Works offline
          </Typography>
        </>
      )}

      {!settings.enabled && (
        <Typography
          variant="caption"
          sx={{
            color: 'rgba(255,255,255,0.5)',
            display: 'block',
            textAlign: 'center',
          }}
        >
          Enable notifications to hear premium sound alerts
        </Typography>
      )}
    </Paper>
  );
};

export default AudioNotificationSettings;